import type {
  IAssistantRow,
  TRowField,
} from '@/interfaces/assistant.interface';
import { changedGroupId, isSizeRow } from './row-presenters';

/*
 * Rows that only work with another row of the same proposal (plan F2.6,
 * P2-3, P2-4, P3-4, P4-1, P5-3): a product created in, or moved to, a
 * category the proposal creates lists that category's row in `requires`, a
 * product that gets an option group the proposal creates lists that group's
 * row, a new size or a size's new name that only another size's removal or
 * rename frees lists that row, and a recipe that uses a stock item the
 * proposal creates lists its row. The Action API refuses an approval that
 * ticks a row without what it requires (422 NEEDS_ROW); the card and the
 * apply action keep the member from getting there. The rows that change one
 * option group may work only together, without `requires` (plan P3-3), and
 * so may the rows that change one product's sizes (plan P4-1): when one
 * starts unticked, the card starts the others unticked too. A row that
 * states a quantity of a stock item whose unit the proposal changes is in
 * the new unit and lists that unit's row in `requires` (plan P5-3, W20
 * decision U1).
 */

/** Changed since, or unusual: the row starts unticked in the review. */
export function isFlagged(row: IAssistantRow): boolean {
  return row.state === 'stale' || row.state === 'outlier';
}

/**
 * The notice when a ticked row needs a row left unticked, by the field of
 * the row it needs (plan P2-3, P3-4, P5-3).
 */
const NEEDS_ROW_KEYS: Readonly<Partial<Record<TRowField, string>>> = {
  newCategory: 'assistant.proposal.needsRow.category',
  newGroup: 'assistant.proposal.needsRow.group',
  newStockItem: 'assistant.proposal.needsRow.stockItem',
  unit: 'assistant.proposal.needsRow.stockUnit',
};

/**
 * The field of the rows of each key family a row can require (plan F6: the
 * part of a key before its first `:`).
 */
const REQUIRED_KEY_FIELDS: ReadonlyMap<string, TRowField> = new Map<
  string,
  TRowField
>([
  ['newCategory', 'newCategory'],
  ['newGroup', 'newGroup'],
  ['newStock', 'newStockItem'],
]);

/**
 * The notice when a ticked row needs a size row left unticked (plan F2.6,
 * P4-1: a new size, or a size's new name, takes a name that only the
 * removal or the rename of another size of the product frees), by the field
 * of the size row it needs. It names that size (`{name}`: «Fugazzeta ·
 * Chica»): the product has others.
 */
const SIZE_NEEDS_ROW_KEYS: ReadonlyMap<string, string> = new Map([
  ['remove', 'assistant.proposal.needsRow.sizeRemove'],
  ['name', 'assistant.proposal.needsRow.sizeRename'],
]);

/** The same notice when the size row it needs cannot be read: it names none. */
const SIZE_NEEDS_ROW_ANY_KEY = 'assistant.proposal.needsRow.sizeAny';

/** A size row's key (plan F6: `item:{id}:size:{sid}:{field}`), its field captured. */
const SIZE_ROW_KEY = /^item:\d+:size:\d+:([A-Za-z]+)$/;

/**
 * A stock item's unit row's key (plan P5-1 `stock:{id}:unit`): its family
 * `stock` also has the item's name, cost, minimum and quantity rows.
 */
const STOCK_UNIT_KEY = /^stock:\d+:unit$/;

/**
 * The unit notice when the unit row it needs cannot be read, or its stock
 * item has no name to show: it names none.
 */
const STOCK_UNIT_ANY_KEY = 'assistant.proposal.needsRow.stockUnitAny';

/**
 * The notice of a ticked row that needs `required`; null for a field without
 * one. A size row's notice names that size (`{name}`, plan P4-1), and a
 * stock item's unit row's notice names that stock item (plan P5-3: the rows
 * that need it state quantities in the new unit).
 */
export function needsRowKey(required: IAssistantRow): string | null {
  if (isSizeRow(required))
    return SIZE_NEEDS_ROW_KEYS.get(required.field) ?? null;
  return NEEDS_ROW_KEYS[required.field] ?? null;
}

/**
 * The needed row of the Action API's 422 NEEDS_ROW (`details.needs`) is a
 * size's removal or rename (plan P4-1), whose notice names that size: the
 * caller reads the row to name it.
 */
export function needsSizeRow(needsKey: string): boolean {
  const field = SIZE_ROW_KEY.exec(needsKey)?.[1];
  return field !== undefined && SIZE_NEEDS_ROW_KEYS.has(field);
}

/**
 * The row whose key is `needsKey` (a required row's, or the Action API's 422
 * NEEDS_ROW `details.needs`) is a stock item's unit change (plan P5-3),
 * whose notice names that stock item: after a NEEDS_ROW, the caller reads
 * the row to name it.
 */
export function needsStockUnitRow(needsKey: string): boolean {
  return STOCK_UNIT_KEY.test(needsKey);
}

/**
 * The notice of an approval the Action API refused because a ticked row
 * needs the row whose key is `needsKey` (422 NEEDS_ROW `details.needs`), by
 * that key's family, naming no row (a size's removal or rename, or a stock
 * item's unit change: its notice without its name); null for a family
 * without one.
 */
export function needsRowKeyOf(needsKey: string): string | null {
  if (needsSizeRow(needsKey)) return SIZE_NEEDS_ROW_ANY_KEY;
  if (needsStockUnitRow(needsKey)) return STOCK_UNIT_ANY_KEY;
  const field = REQUIRED_KEY_FIELDS.get(needsKey.split(':')[0]);
  return field ? NEEDS_ROW_KEYS[field] ?? null : null;
}

/** By existing option group, the rows that change its content (plan P3-1). */
function rowsByChangedGroup(
  rows: readonly IAssistantRow[]
): Map<number, IAssistantRow[]> {
  const byGroup = new Map<number, IAssistantRow[]>();
  for (const row of rows) {
    const groupId = changedGroupId(row);
    if (groupId === null) continue;
    byGroup.set(groupId, [...(byGroup.get(groupId) ?? []), row]);
  }
  return byGroup;
}

/** By product, the rows that change its sizes (plan P4-1). */
function rowsBySizedProduct(
  rows: readonly IAssistantRow[]
): Map<number, IAssistantRow[]> {
  const byProduct = new Map<number, IAssistantRow[]>();
  for (const row of rows) {
    if (row.itemId === null || !isSizeRow(row)) continue;
    byProduct.set(row.itemId, [...(byProduct.get(row.itemId) ?? []), row]);
  }
  return byProduct;
}

/**
 * By row key, the row each row needs that starts unticked: a flagged row, or
 * one that needs such a row itself. Each of them starts unticked too, so the
 * card's default selection never ticks a row without what it needs. The
 * rows that change one existing option group need each other without
 * `requires`: the Action API refuses an approval whose ticked rows of a
 * group leave it broken (plan P3-3, 422 NO_ACTIVE_CHOICE / RULES_INVALID),
 * so when one of them starts unticked, the others do too. The same holds
 * for the rows that change one product's sizes (plan P4-1, 422
 * NO_ACTIVE_SIZE / SIZE_LIMIT: e.g. a pause that only leaves a size on sale
 * with a new size flagged as a taken name). A row flagged itself keeps its
 * own reason and is not listed; a required key with no row is left to the
 * Action API.
 */
export function rowsNeedingUnticked(
  rows: readonly IAssistantRow[]
): Map<string, IAssistantRow> {
  const byKey = new Map(rows.map((row) => [row.key, row]));
  const byGroup = rowsByChangedGroup(rows);
  const bySizedProduct = rowsBySizedProduct(rows);
  const needing = new Map<string, IAssistantRow>();
  const startsUnticked = (row: IAssistantRow) =>
    isFlagged(row) || needing.has(row.key);
  /** Another row of `together` that starts unticked. */
  const otherUnticked = (
    row: IAssistantRow,
    together: readonly IAssistantRow[] | undefined
  ) => together?.find((other) => other !== row && startsUnticked(other));
  /**
   * A row it requires, else another row of its group, else another size row
   * of its product, that starts unticked.
   */
  const neededUnticked = (row: IAssistantRow) => {
    const required = (row.requires ?? [])
      .map((key) => byKey.get(key))
      .find(
        (candidate): candidate is IAssistantRow =>
          candidate !== undefined && startsUnticked(candidate)
      );
    if (required) return required;
    const groupId = changedGroupId(row);
    if (groupId !== null) return otherUnticked(row, byGroup.get(groupId));
    return row.itemId !== null && isSizeRow(row)
      ? otherUnticked(row, bySizedProduct.get(row.itemId))
      : undefined;
  };
  // Until nothing changes: a row can need one that needs a flagged row.
  let changed = true;
  while (changed) {
    changed = false;
    for (const row of rows) {
      if (startsUnticked(row)) continue;
      const required = neededUnticked(row);
      if (required) {
        needing.set(row.key, required);
        changed = true;
      }
    }
  }
  return needing;
}

/**
 * The rows the card starts ticked, i.e. what «Aplicar» applies as the card
 * first shows it: neither flagged nor needing a row that starts unticked.
 */
export function rowsTickedByDefault(
  rows: readonly IAssistantRow[]
): IAssistantRow[] {
  const needing = rowsNeedingUnticked(rows);
  return rows.filter((row) => !isFlagged(row) && !needing.has(row.key));
}

/** Every key some row requires, once each, in the order the rows name them. */
export function requiredKeys(rows: readonly IAssistantRow[]): string[] {
  return Array.from(new Set(rows.flatMap((row) => row.requires ?? [])));
}

/**
 * The first row (in view order) that a ticked row needs and the member left
 * unticked; null when every ticked row has what it needs. A required key
 * with no row is left to the Action API.
 */
export function unmetRequirement(
  rows: readonly IAssistantRow[],
  selected: readonly string[]
): IAssistantRow | null {
  const ticked = new Set(selected);
  const byKey = new Map(rows.map((row) => [row.key, row]));
  for (const row of rows) {
    if (!ticked.has(row.key)) continue;
    for (const key of row.requires ?? []) {
      const required = byKey.get(key);
      if (required && !ticked.has(key)) return required;
    }
  }
  return null;
}
